import openaiService from './openaiService';
import { getApiKey } from './authService';
import { Recipe } from '../models/Recipe';
import { validateAndParseJSON } from '../utils/typeGuards';

export type RecipeDifficulty = 'beginner' | 'easy' | 'intermediate' | 'advanced' | 'expert';
export type CuisineType = 'italian' | 'french' | 'chinese' | 'japanese' | 'mexican' | 'indian' | 'thai' | 'american' | 'mediterranean' | 'fusion' | 'other';
export type DietaryRestriction = 'vegan' | 'vegetarian' | 'gluten-free' | 'dairy-free' | 'nut-free' | 'low-carb' | 'keto' | 'paleo' | 'halal' | 'kosher';
export type RecipeCategory = 'breakfast' | 'lunch' | 'dinner' | 'dessert' | 'snack' | 'appetizer' | 'side-dish' | 'beverage' | 'sauce' | 'soup' | 'salad';

export interface RecipeAnalysis {
  difficulty: {
    level: RecipeDifficulty;
    score: number; // 1-10
    reasoning: string;
    techniques: string[];
    estimatedSkillTime: number; // minutes for a beginner
  };
  cuisine: {
    primary: CuisineType;
    secondary?: CuisineType;
    confidence: number; // 0-1
    culturalNotes?: string;
  };
  dietary: {
    restrictions: DietaryRestriction[];
    warnings: string[];
    allergens: string[];
    isHealthy: boolean;
    nutritionHighlights: string[];
  };
  category: {
    primary: RecipeCategory;
    secondary?: RecipeCategory;
    mealTime: string[];
    occasion?: string[];
  };
  chefTips: string[];
}

class RecipeAnalysisService {
  /**
   * Analyze a recipe for difficulty, cuisine, dietary info, and more
   */
  async analyzeRecipe(recipe: Recipe): Promise<RecipeAnalysis> {
    try {
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      const prompt = this.buildAnalysisPrompt(recipe);
      const response = await openaiService.getChatCompletion(prompt);
      
      return this.parseAnalysisResponse(response);
    } catch (error) {
      console.error('Error analyzing recipe:', error);
      // Return a fallback analysis
      return this.getFallbackAnalysis(recipe);
    }
  }

  /**
   * Calculate recipe difficulty based on heuristics
   */
  calculateDifficulty(recipe: Recipe): { level: RecipeDifficulty; score: number } {
    let score = 0;
    
    // Check ingredients complexity
    const ingredients = recipe.ingredients.split('\n').filter(i => i.trim());
    if (ingredients.length > 15) {
      score += 2;
    }
    else if (ingredients.length > 10) {
      score += 1;
    }
    
    // Check for complex techniques in steps
    const complexTechniques = [
      'julienne', 'brunoise', 'chiffonade', 'blanch', 'deglaze', 'emulsify',
      'fold', 'temper', 'caramelize', 'flambé', 'poach', 'braise', 'confit',
      'sous vide', 'ferment', 'proof', 'laminate', 'clarify', 'reduce'
    ];
    
    const stepsText = recipe.steps?.map(s => s.content).join(' ').toLowerCase() || '';
    complexTechniques.forEach(technique => {
      if (stepsText.includes(technique)) {
        score += 1;
      }
    });
    
    // Check for timing complexity
    if (stepsText.includes('overnight') || stepsText.includes('24 hour')) {
      score += 2;
    }
    if (stepsText.includes('meanwhile') || stepsText.includes('simultaneously')) {
      score += 1;
    }
    
    // Map score to difficulty level
    let level: RecipeDifficulty;
    if (score <= 2) {
      level = 'beginner';
    }
    else if (score <= 4) {
      level = 'easy';
    }
    else if (score <= 6) {
      level = 'intermediate';
    }
    else if (score <= 8) {
      level = 'advanced';
    }
    else {
      level = 'expert';
    }
    
    return { level, score: Math.min(score, 10) };
  }

  /**
   * Detect cuisine type from ingredients and recipe name
   */
  detectCuisine(recipe: Recipe): CuisineType {
    const title = recipe.title.toLowerCase();
    const ingredients = recipe.ingredients.toLowerCase();
    
    // Italian indicators
    if (title.includes('pasta') || title.includes('pizza') || 
        ingredients.includes('parmesan') || ingredients.includes('mozzarella') ||
        ingredients.includes('basil') && ingredients.includes('tomato')) {
      return 'italian';
    }
    
    // Asian cuisine indicators
    if (ingredients.includes('soy sauce') || ingredients.includes('sesame') || 
        ingredients.includes('ginger') && ingredients.includes('garlic')) {
      if (ingredients.includes('miso') || ingredients.includes('sake')) {
        return 'japanese';
      }
      if (ingredients.includes('fish sauce') || ingredients.includes('lemongrass')) {
        return 'thai';
      }
      return 'chinese';
    }
    
    // Mexican indicators
    if (ingredients.includes('cilantro') && (ingredients.includes('lime') || 
        ingredients.includes('jalapeño') || ingredients.includes('tortilla'))) {
      return 'mexican';
    }
    
    // Indian indicators
    if (ingredients.includes('curry') || ingredients.includes('turmeric') || 
        ingredients.includes('cumin') && ingredients.includes('coriander')) {
      return 'indian';
    }
    
    // French indicators
    if (title.includes('french') || ingredients.includes('beurre') || 
        ingredients.includes('wine') && ingredients.includes('butter')) {
      return 'french';
    }
    
    return 'other';
  }

  /**
   * Detect dietary restrictions and allergens
   */
  detectDietaryInfo(recipe: Recipe): { restrictions: DietaryRestriction[]; allergens: string[] } {
    const restrictions: DietaryRestriction[] = [];
    const allergens: string[] = [];
    const ingredients = recipe.ingredients.toLowerCase();
    
    // Check for vegan/vegetarian
    const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'fish', 'salmon', 'tuna', 'shrimp', 'bacon'];
    const hasMeat = meatKeywords.some(meat => ingredients.includes(meat));
    
    const dairyKeywords = ['milk', 'cream', 'cheese', 'butter', 'yogurt'];
    const hasDairy = dairyKeywords.some(dairy => ingredients.includes(dairy));
    
    const eggKeywords = ['egg', 'eggs'];
    const hasEggs = eggKeywords.some(egg => ingredients.includes(egg));
    
    if (!hasMeat && !hasDairy && !hasEggs) {
      restrictions.push('vegan');
      restrictions.push('vegetarian');
    } else if (!hasMeat) {
      restrictions.push('vegetarian');
    }
    
    // Check for gluten-free
    const glutenKeywords = ['flour', 'bread', 'pasta', 'wheat', 'barley', 'rye'];
    if (!glutenKeywords.some(gluten => ingredients.includes(gluten))) {
      restrictions.push('gluten-free');
    }
    
    // Check for dairy-free
    if (!hasDairy) {
      restrictions.push('dairy-free');
    }
    
    // Check for common allergens
    if (ingredients.includes('peanut') || ingredients.includes('almond') || 
        ingredients.includes('walnut') || ingredients.includes('cashew')) {
      allergens.push('nuts');
    } else {
      restrictions.push('nut-free');
    }
    
    if (ingredients.includes('soy')) {
      allergens.push('soy');
    }
    if (hasEggs) {
      allergens.push('eggs');
    }
    if (hasDairy) {
      allergens.push('dairy');
    }
    if (ingredients.includes('shellfish') || ingredients.includes('shrimp')) {
      allergens.push('shellfish');
    }
    
    return { restrictions, allergens };
  }

  /**
   * Generate chef tips for a recipe
   */
  async generateChefTips(recipe: Recipe): Promise<string[]> {
    try {
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      const prompt = `As a professional chef, provide 3-5 concise, practical tips for making "${recipe.title}" successfully. Focus on technique improvements, ingredient handling, or common mistakes to avoid. Each tip should be 1-2 sentences maximum.

Return ONLY a JSON array of strings like this:
["Tip 1", "Tip 2", "Tip 3"]`;

      const response = await openaiService.getChatCompletion(prompt);
      
      try {
        const tips = JSON.parse(response);
        if (Array.isArray(tips)) {
          return tips.slice(0, 5);
        }
      } catch {
        // Fallback to parsing as text
        return response.split('\n')
          .filter(line => line.trim())
          .map(line => line.replace(/^[-*\d.]\s*/, ''))
          .slice(0, 5);
      }
    } catch (error) {
      console.error('Error generating chef tips:', error);
    }
    
    // Return generic tips as fallback
    return [
      'Mise en place: Prepare all ingredients before you start cooking',
      'Taste as you go and adjust seasoning accordingly',
      'Let meat rest after cooking for juicier results'
    ];
  }

  private buildAnalysisPrompt(recipe: Recipe): string {
    const ingredients = recipe.ingredients || 'No ingredients provided';
    const steps = recipe.steps?.map(s => s.content).join('\n') || 'No steps provided';
    
    return `Analyze this recipe and provide a comprehensive analysis in JSON format:

Recipe: "${recipe.title}"
Ingredients:
${ingredients}

Steps:
${steps}

Please analyze and return ONLY a JSON object with this exact structure:
{
  "difficulty": {
    "level": "beginner" | "easy" | "intermediate" | "advanced" | "expert",
    "score": 1-10,
    "reasoning": "brief explanation",
    "techniques": ["technique1", "technique2"],
    "estimatedSkillTime": minutes_for_beginner
  },
  "cuisine": {
    "primary": "italian" | "french" | "chinese" | "japanese" | "mexican" | "indian" | "thai" | "american" | "mediterranean" | "fusion" | "other",
    "secondary": "optional_secondary_cuisine",
    "confidence": 0.0-1.0,
    "culturalNotes": "optional cultural context"
  },
  "dietary": {
    "restrictions": ["vegan", "vegetarian", "gluten-free", etc],
    "warnings": ["contains raw fish", "high sodium", etc],
    "allergens": ["nuts", "dairy", "eggs", etc],
    "isHealthy": true/false,
    "nutritionHighlights": ["high protein", "low calorie", etc]
  },
  "category": {
    "primary": "breakfast" | "lunch" | "dinner" | "dessert" | "snack" | "appetizer" | "side-dish" | "beverage" | "sauce" | "soup" | "salad",
    "secondary": "optional_secondary_category",
    "mealTime": ["breakfast", "lunch", "dinner"],
    "occasion": ["weeknight", "special occasion", "party", etc]
  },
  "chefTips": [
    "Practical tip 1",
    "Practical tip 2",
    "Practical tip 3"
  ]
}`;
  }

  private parseAnalysisResponse(response: string): RecipeAnalysis {
    const fallback = this.getFallbackAnalysis({ 
      id: '', 
      title: '', 
      ingredients: '', 
      notes: '', 
      userId: '', 
      createdAt: new Date(), 
      updatedAt: new Date(), 
      isFavorite: false 
    } as unknown as Recipe);
    
    try {
      const parsed = validateAndParseJSON(
        response,
        (data): data is RecipeAnalysis => {
          if (!data || typeof data !== 'object') {
            return false;
          }
          const analysis = data as any;
          return !!(
            analysis.difficulty && 
            analysis.cuisine && 
            analysis.dietary && 
            analysis.category &&
            Array.isArray(analysis.chefTips)
          );
        },
        fallback
      );
      
      return parsed;
    } catch (error) {
      console.error('Error parsing recipe analysis:', error);
      return fallback;
    }
  }

  private getFallbackAnalysis(recipe: Recipe): RecipeAnalysis {
    const difficulty = this.calculateDifficulty(recipe);
    const cuisine = this.detectCuisine(recipe);
    const dietary = this.detectDietaryInfo(recipe);
    
    return {
      difficulty: {
        level: difficulty.level,
        score: difficulty.score,
        reasoning: 'Analysis based on recipe complexity',
        techniques: [],
        estimatedSkillTime: difficulty.score * 15
      },
      cuisine: {
        primary: cuisine,
        confidence: 0.7,
      },
      dietary: {
        restrictions: dietary.restrictions,
        warnings: [],
        allergens: dietary.allergens,
        isHealthy: dietary.restrictions.includes('vegan') || dietary.restrictions.includes('vegetarian'),
        nutritionHighlights: []
      },
      category: {
        primary: 'dinner',
        mealTime: ['dinner']
      },
      chefTips: [
        'Read through the entire recipe before starting',
        'Prepare all ingredients before you begin cooking',
        'Taste and adjust seasoning as you go'
      ]
    };
  }
}

export default new RecipeAnalysisService();
